import { ReactNode, useState } from 'react';
import UpsellTrigger from '@alltrails/analytics/enums/UpsellTrigger';
import Typography from '@alltrails/shared/denali/components/Typography';
import useUser from 'hooks/useUser';
import PurchaseTier from 'types/PurchaseTier';
import useExperiments from 'hooks/useExperiments';
import ProductsAvailable from '@alltrails/analytics/enums/ProductsAvailable';
import BaseUpsellModal from '../UpsellSystem/BaseUpsell/BaseUpsellModal';
import PlanSelection from '../PlanSelection/PlanSelection';
import PeakFeatureList from '../UpsellSystem/PeakFeatureList/PeakFeatureList';
import getCtaText from '../UpsellSystem/getCtaText';
import * as styles from './GenericUpsell.module.scss';
import PeakSubheading from './PeakSubheading';
import PeakLogoBadge from '../SubscriptionBadge/Logo/Peak';

type Props = {
  heading: ReactNode;
  isOpen: boolean;
  onCloseRequest: () => void;
  promoEligibleHeading: ReactNode;
  returnTo?: string;
  upsellTrigger: UpsellTrigger;
};

// https://www.figma.com/design/PtYSSpohQ9rL7ZWCcdsYJK/2025-Upsell-System?node-id=5543-297520&t=9h0uykTwgb7vGafI-0
export default function GenericUpsell({ heading, promoEligibleHeading, onCloseRequest, isOpen, upsellTrigger, returnTo }: Props) {
  const user = useUser();
  const isPromoEligible = user?.isPromoEligible;
  const experiments = useExperiments();
  const isPlusDefault = experiments['rm-peak-soft-launch']?.value === 'plus_default';
  const defaultTier = experiments['rm-peak-soft-launch']?.value === 'peak_default' ? 'peak' : 'plus';
  const isPlus = user?.subscriptionTier === 'plus';
  const [tierSelected, setTierSelected] = useState<PurchaseTier>(isPlus ? 'peak' : defaultTier);

  return (
    <BaseUpsellModal
      primaryButtonText={getCtaText(tierSelected, isPromoEligible)}
      isOpen={isOpen}
      onCloseRequest={onCloseRequest}
      returnTo={returnTo}
      upsellTrigger={upsellTrigger}
      tierSelected={tierSelected}
      productsAvailable={isPlus ? ProductsAvailable.Peak : ProductsAvailable.PlusPeak}
    >
      <>
        {isPlus ? (
          <>
            <PeakLogoBadge size="md" variant="full" className={styles.logo} />
            <Typography variant="heading400">{isPromoEligible ? promoEligibleHeading : heading}</Typography>
            <PeakSubheading />
          </>
        ) : (
          <>
            <Typography variant="heading400">{isPromoEligible ? promoEligibleHeading : heading}</Typography>
            <PlanSelection showPlusFirst={isPlusDefault} tierSelected={tierSelected} setTierSelected={setTierSelected} />
          </>
        )}
        <PeakFeatureList tierSelected={tierSelected} upsellTrigger={upsellTrigger} />
      </>
    </BaseUpsellModal>
  );
}
